import React, { useState, useEffect } from 'react';
import {
  Drawer,
  Box,
  Typography,
  Button,
  TextField,
  Autocomplete,
  InputAdornment,
  Checkbox,
  FormControlLabel,
} from '@material-ui/core';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { casesApi } from 'src/api/casesApi';
import toast from 'react-hot-toast';
import { makeStyles } from '@material-ui/core/styles';
import { projectModulesApi } from 'src/api/projectModules';

interface ProjectModuleDrawerProps {
  open: boolean;
  onClose: () => void;
  module?: any;
  onSuccess: () => void;
}

const validationSchema = Yup.object().shape({
  organization: Yup.object().required('Organization is required'),
  project: Yup.object().required('Project is required'),
  name: Yup.string().required('Module name is required'),
});

// Add styles
const useStyles = makeStyles(() => ({
  drawer: {
    '& h4': {
      background: '#C22027',
      color: '#fff',
      padding: '0 40px',
      margin: '-5px -20px 30px',
      height: '64px',
      display: 'flex',
      alignItems: 'center',
    },
    '& .drawerContent': {
      padding: '5px 20px',
    },
  },
}));

const ProjectModuleDrawer: React.FC<ProjectModuleDrawerProps> = ({
  open,
  onClose,
  module,
  onSuccess,
}) => {
  const classes = useStyles();
  const [organizations, setOrganizations] = useState<Array<any>>([]);
  const [projects, setProjects] = useState<Array<any>>([]);
  const [orgInputValue, setOrgInputValue] = useState<string>('');
  const [projectInputValue, setProjectInputValue] = useState<string>('');
  const [keepOpen, setKeepOpen] = useState(false);

  const isEditMode = Boolean(module);

  // Extract project key from module name if in edit mode

  const formik = useFormik({
    initialValues: {
      organization: module?.organization || null,
      project: module?.project || null,
      name: module?.name?.split('- ').slice(1).join('') || '',
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      try {
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        const moduleNameWithPrefix = `${getProjectPrefix()}- ${values.name}`;

        if (isEditMode) {
          // Update API call
          const response = await projectModulesApi.updateModule(module.id, {
            orgId: values.organization.id,
            projectId: values.project.id,
            name: moduleNameWithPrefix,
          });
          if (response?.status === 200) {
            toast.success('Module updated successfully');
          } else {
            toast.error('Failed to update module');
          }
        } else {
          // Create API call
          const response = await projectModulesApi.createModule({
            orgId: values.organization.id,
            projectId: values.project.id,
            name: moduleNameWithPrefix,
          });
          if (response?.status === 201) {
            toast.success('Module created successfully');
          } else {
            toast.error('Failed to create module');
          }
        }

        onSuccess();
        if (!keepOpen) {
          onClose();
          formik.resetForm();
        } else {
          formik.setFieldValue('name', '');
          formik.setFieldTouched('name', false);
        }
      } catch (error) {
        toast.error(`Failed to ${isEditMode ? 'update' : 'create'} module`);
        console.error(
          `Error ${isEditMode ? 'updating' : 'creating'} module:`,
          error
        );
      }
    },
  });

  function getProjectPrefix() {
    if (isEditMode && module?.name && !formik?.values?.project?.key) {
      return module.name.split('-')[0];
    }
    return `${formik.values.project?.key} ` || '';
  }

  const getAllOrganizations = async () => {
    try {
      const response = await casesApi.getOrganizationId();
      if (response?.status === 200) {
        setOrganizations(response?.data);
      }
    } catch (error) {
      console.error('Error fetching organizations:', error);
      setOrganizations([]);
    }
  };

  const getAllProjects = async (orgId: string) => {
    try {
      const response = await casesApi.getProjectsById(orgId);
      if (response?.status === 200) {
        setProjects(response?.data);
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
      setProjects([]);
    }
  };

  useEffect(() => {
    if (open) {
      getAllOrganizations();
    }
  }, [open]);

  useEffect(() => {
    if (module?.organization) {
      getAllProjects(module.organization.id);
    }
  }, [module]);

  const handleClose = () => {
    formik.resetForm();
    onClose();
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      PaperProps={{
        sx: { width: '500px' },
        className: classes.drawer,
      }}>
      <Box className="drawerContent">
        <Typography variant="h4">
          {isEditMode ? 'Edit Project Module' : 'Create Project Module'}
        </Typography>

        <form onSubmit={formik.handleSubmit}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Autocomplete
              size="small"
              id="organization-select"
              options={organizations}
              value={formik.values.organization}
              onChange={(_, value) => {
                formik.setFieldValue('organization', value);
                formik.setFieldValue('project', null);
                if (value) {
                  getAllProjects(value.id);
                } else {
                  setProjects([]);
                }
              }}
              getOptionLabel={(option) => option.name}
              inputValue={orgInputValue}
              onInputChange={(_, newValue) => {
                setOrgInputValue(newValue);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  variant="outlined"
                  placeholder="Organization*"
                  error={Boolean(
                    formik.touched.organization && formik.errors.organization
                  )}
                  helperText={
                    formik.touched.organization && formik.errors.organization
                  }
                />
              )}
            />

            <Autocomplete
              size="small"
              id="project-select"
              options={projects}
              value={formik.values.project}
              onChange={(_, value) => {
                formik.setFieldValue('project', value);
              }}
              getOptionLabel={(option) => option.name}
              inputValue={projectInputValue}
              onInputChange={(_, newValue) => {
                setProjectInputValue(newValue);
              }}
              disabled={!formik.values.organization}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  variant="outlined"
                  placeholder="Project*"
                  error={Boolean(
                    formik.touched.project && formik.errors.project
                  )}
                  helperText={formik.touched.project && formik.errors.project}
                />
              )}
            />

            <TextField
              size="small"
              fullWidth
              variant="outlined"
              placeholder={
                formik.values.project
                  ? 'Module Name*'
                  : 'Select a project first*'
              }
              name="name"
              value={formik.values.name}
              onChange={formik.handleChange}
              disabled={!formik.values.project}
              error={Boolean(formik.touched.name && formik.errors.name)}
              helperText={formik.touched.name && formik.errors.name}
              InputProps={{
                startAdornment: formik.values.project && (
                  <InputAdornment position="start">
                    <Typography
                      sx={{
                        color: '#666',
                        fontSize: '14px',
                      }}>
                      {getProjectPrefix()}-
                    </Typography>
                  </InputAdornment>
                ),
              }}
            />

            {!isEditMode && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={keepOpen}
                    onChange={(e) => setKeepOpen(e.target.checked)}
                    color="secondary"
                  />
                }
                label="Keep dialog open after submission"
              />
            )}

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 2,
                mt: 3,
              }}>
              <Button variant="outlined" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                variant="contained"
                color="secondary"
                type="submit"
                disabled={formik.isSubmitting}>
                {isEditMode ? 'Update' : 'Create'}
              </Button>
            </Box>
          </Box>
        </form>
      </Box>
    </Drawer>
  );
};

export default ProjectModuleDrawer;
